import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import GridHeaderComponent from "@/ui/molecules/GridHeaderComponent";
import GridEditChartOfAccountsDropdownItemAccounts from "./GridEditChartOfAccountsDropdownItemAccounts";
import type {
    ColDef,
    ICellRendererParams,
    ValueSetterParams,
} from "@/ui/molecules/Grid";
import { Button, Tooltip } from "@proda-ai/ui-kit";
import type { TResponseGetCoaDefinitionByCoaId } from "@/apis/odin/apiODIN";
import { isNullable, isTruthy } from "@/utils/guards";
import type { TAccountDTO } from "@/store/accounts/accountTypes";

export const gridEditChartOfAccountsColumnDefs = (
    deleteAccount: (accountId: number) => void,
    restoreAccount: (accountId: number) => void,
    showValidationErrors: boolean,
    isEditable: boolean,
    errors: Record<number, Record<string, string>>,
    coaDefinition: TResponseGetCoaDefinitionByCoaId,
    optionsAccountsArray: { value: string; label: string }[],
    accountsToBeDeleted: number[],
): ColDef[] => {
    const optionsAccountsValues = optionsAccountsArray.map(
        option => option.value,
    );
    const optionsAccounts = optionsAccountsArray.reduce(
        (acc, option) => ({ ...acc, [option.value]: option.label }),
        {} as Record<string, string>,
    );
    const commonCellStyles = {
        paddingLeft: "0px",
        paddingRight: "0px",
    };

    return [
        ...(isEditable
            ? [
                  {
                      headerName: "",
                      minWidth: 10,
                      maxWidth: 10,
                      editable: false,
                      cellEditor: "agTextCellEditor",
                      cellStyle: commonCellStyles,
                      cellEditorParams: {
                          placeholder: "Enter account code",
                      },
                      cellRenderer: (
                          params: ICellRendererParams<TAccountDTO>,
                      ) => {
                          if (isNullable(params.data)) return null;
                          return (
                              <Box
                                  sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "left",
                                      height: "100%",
                                      width: "100%",
                                      backgroundColor:
                                          accountsToBeDeleted.includes(
                                              params.data.id,
                                          )
                                              ? "var(--proda-red)"
                                              : params.data.draft === true
                                                ? "var(--proda-green-120)"
                                                : params.data.isEdited === true
                                                  ? "var(--proda-yellow)"
                                                  : "var(--proda-black-10)",
                                  }}
                              />
                          );
                      },
                  },
              ]
            : []),
        {
            headerName: "Account Code",
            field: "code",
            maxWidth: 150,
            minWidth: 150,
            editable: isEditable,
            cellEditor: "agTextCellEditor",
            cellStyle: commonCellStyles,
            cellRenderer: (params: ICellRendererParams<TAccountDTO>) => {
                if (!params.data) return null;
                const errorMsg = errors[params.data.id]?.["code"];
                const shouldShowTooltip =
                    showValidationErrors && isTruthy(errorMsg);
                const content = (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            paddingRight: "16px",
                            paddingLeft: "16px",
                            ...(shouldShowTooltip === true && {
                                backgroundColor: "var(--proda-red-20)",
                                borderColor: "var(--proda-red)",
                                borderWidth: "2px",
                                borderStyle: "solid",
                            }),
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: isTruthy(params.value)
                                    ? "var(--proda-black)"
                                    : "var(--proda-black-10)",
                            }}
                        >
                            {isTruthy(params.value)
                                ? params.value
                                : "*required"}
                        </Typography>
                    </Box>
                );
                if (shouldShowTooltip) {
                    return (
                        <Tooltip
                            content={
                                <Typography variant="body2">
                                    {errorMsg}
                                </Typography>
                            }
                            style={{
                                width: "100%",
                                height: "100%",
                            }}
                        >
                            {content}
                        </Tooltip>
                    );
                }
                return content;
            },
            valueSetter: (params: ValueSetterParams) => {
                if (isNullable(params.data)) return false;
                if (isNullable(params.colDef)) return false;
                const field = (params.colDef as { field?: string }).field;
                if (isNullable(field)) return false;
                const newValue =
                    typeof params.newValue === "string"
                        ? params.newValue.trim()
                        : "";
                (params.data as Record<string, unknown>)[field] = newValue;
                return true;
            },
        },
        {
            headerName: "Account Mapping",
            field: "account",
            editable: isEditable,
            minWidth: 200,
            cellEditor: "agTextCellEditor",
            cellStyle: commonCellStyles,
            cellRenderer: (params: ICellRendererParams<TAccountDTO>) => {
                if (isNullable(params.data)) return null;
                const errorMsg = errors[params.data.id]?.["account"];
                const shouldShowTooltip =
                    showValidationErrors && isTruthy(errorMsg);
                const content = (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            paddingRight: "16px",
                            paddingLeft: "16px",
                            ...(shouldShowTooltip && {
                                backgroundColor: "var(--proda-red-20)",
                                borderColor: "var(--proda-red)",
                                borderWidth: "2px",
                                borderStyle: "solid",
                            }),
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: isTruthy(params.value)
                                    ? "var(--proda-black-10)"
                                    : "var(--proda-black)",
                            }}
                        >
                            {isTruthy(params.value)
                                ? params.value
                                : "*required"}
                        </Typography>
                    </Box>
                );
                if (shouldShowTooltip) {
                    return (
                        <Tooltip
                            content={
                                <Typography variant="body2">
                                    {errorMsg}
                                </Typography>
                            }
                            style={{
                                width: "100%",
                                height: "100%",
                            }}
                        >
                            {content}
                        </Tooltip>
                    );
                }
                return content;
            },
            cellEditorParams: {
                placeholder: "Select account",
            },
            valueSetter: (params: ValueSetterParams) => {
                if (isNullable(params.data)) return false;
                if (isNullable(params.colDef)) return false;
                const field = (params.colDef as { field?: string }).field;
                if (isNullable(field)) return false;
                const newValue =
                    typeof params.newValue === "string"
                        ? params.newValue.trim()
                        : "";
                (params.data as Record<string, unknown>)[field] = newValue;
                return true;
            },
        },
        {
            headerName: "Type",
            headerComponent: GridHeaderComponent,
            field: "type",
            cellEditor: "agRichSelectCellEditor",
            editable: isEditable,
            width: 150,
            minWidth: 150,
            maxWidth: 180,
            cellEditorParams: {
                allowTyping: false,
                valueListMaxHeight: 220,
                cellHeight: 48,
                cellRenderer: (params: ICellRendererParams) => (
                    <GridEditChartOfAccountsDropdownItemAccounts
                        optionsAccountsArray={optionsAccountsArray}
                        displayRightIcon={false}
                        {...params}
                    />
                ),
                values: optionsAccountsValues,
            },
            refData: optionsAccounts,
            cellRenderer: (params: ICellRendererParams) => (
                <GridEditChartOfAccountsDropdownItemAccounts
                    optionsAccountsArray={optionsAccountsArray}
                    displayRightIcon={isEditable}
                    {...params}
                />
            ),
        },
        {
            headerName: "Section",
            field: "section",
            minWidth: 200,
            editable: isEditable,
            cellRenderer: (params: ICellRendererParams<TAccountDTO>) => {
                if (isNullable(params.data)) return null;
                return (
                    <Typography
                        variant="body2"
                        sx={{
                            color: isTruthy(params.value)
                                ? "var(--proda-black)"
                                : "var(--proda-black-10)",
                        }}
                    >
                        {isTruthy(params.value)
                            ? params.value
                            : isTruthy(params.data.draft)
                              ? "optional"
                              : ""}
                    </Typography>
                );
            },
            valueSetter: (params: ValueSetterParams) => {
                if (isNullable(params.data)) return false;
                if (isNullable(params.colDef)) return false;
                const field = (params.colDef as { field?: string }).field;
                if (isNullable(field)) return false;
                const newValue =
                    typeof params.newValue === "string"
                        ? params.newValue.trim()
                        : "";
                (params.data as Record<string, unknown>)[field] = newValue;
                return true;
            },
        },
        ...(isTruthy(coaDefinition.hasSubcategory)
            ? [
                  {
                      headerName: "Sub-category",
                      field: "subcategory",
                      minWidth: 200,
                      editable: isEditable,
                      cellRenderer: (
                          params: ICellRendererParams<TAccountDTO>,
                      ) => {
                          if (isNullable(params.data)) return null;
                          return (
                              <Typography
                                  variant="body2"
                                  sx={{
                                      color: !isNullable(params.value)
                                          ? "var(--proda-black)"
                                          : "var(--proda-black-10)",
                                  }}
                              >
                                  {!isNullable(params.value)
                                      ? params.value
                                      : isTruthy(params.data.draft)
                                        ? "optional"
                                        : ""}
                              </Typography>
                          );
                      },
                      valueSetter: (params: ValueSetterParams) => {
                          if (isNullable(params.data)) return false;
                          if (isNullable(params.colDef)) return false;
                          const field = (params.colDef as { field?: string })
                              .field;
                          if (isNullable(field)) return false;
                          const newValue =
                              typeof params.newValue === "string"
                                  ? params.newValue.trim()
                                  : "";
                          (params.data as Record<string, unknown>)[field] =
                              newValue;
                          return true;
                      },
                  },
              ]
            : []),

        ...(isEditable
            ? [
                  {
                      headerName: "Actions",
                      minWidth: 100,
                      maxWidth: 100,
                      suppressMenu: true,
                      suppressMovable: true,
                      suppressSizeToFit: true,
                      cellRenderer: (
                          params: ICellRendererParams<TAccountDTO>,
                      ) => {
                          if (isNullable(params.data)) return null;
                          return (
                              <Box
                                  sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      height: "100%",
                                  }}
                              >
                                  {!accountsToBeDeleted.includes(
                                      params.data.id,
                                  ) ? (
                                      <Button
                                          size="small"
                                          pattern="danger"
                                          onClick={e => {
                                              e.stopPropagation();
                                              if (isNullable(params.data))
                                                  return;
                                              deleteAccount(params.data.id);
                                          }}
                                          disabled={!isEditable}
                                      >
                                          Delete
                                      </Button>
                                  ) : (
                                      <Button
                                          size="small"
                                          pattern="primary"
                                          onClick={e => {
                                              e.stopPropagation();
                                              if (isNullable(params.data))
                                                  return;
                                              restoreAccount(params.data.id);
                                          }}
                                          disabled={!isEditable}
                                      >
                                          Restore
                                      </Button>
                                  )}
                              </Box>
                          );
                      },
                  },
              ]
            : []),
    ];
};
